import {
  ALL_SHORTCUT_TYPES,
  ALL_WIDGET_TYPES,
  MAX_ENABLED_SHORTCUTS,
} from './home-screen.constants'
import {
  HomepageConfigResponse,
  HomepagePosition,
  HomepageShortcut,
  HomepageWidgetType,
  HomepageWidgetTypeEnum,
  ShortcutType,
} from './home-screen.types'

const reindexByPosition = <T extends { position: number }>(items: T[]): T[] =>
  items.map((item, index) => ({ ...item, position: index }))

export const sortByPosition = <T extends { position: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.position - b.position)

// Widgets/shortcuts: active and inactive items share one draggable list, so order is plain
// (no `position` field) and only dragging - never toggling - changes it.
export const moveInOrder = <T>(
  order: T[],
  sourceIndex: number,
  targetIndex: number,
): T[] | null => {
  const reordered = [...order]
  const [moved] = reordered.splice(sourceIndex, 1)
  if (moved === undefined) return null
  reordered.splice(targetIndex, 0, moved)
  return reordered
}

// Services has no inactive list, so it stays position-keyed and gets reindexed on every move.
const moveAndReindex = <T extends { position: number }>(
  sorted: T[],
  sourceIndex: number,
  targetIndex: number,
): T[] | null => {
  const reordered = [...sorted]
  const [moved] = reordered.splice(sourceIndex, 1)
  if (!moved) return null
  reordered.splice(targetIndex, 0, moved)
  return reindexByPosition(reordered)
}

export const isWidgetEnabled = (
  config: HomepageConfigResponse,
  widget: HomepageWidgetType,
): boolean => {
  switch (widget) {
    case HomepageWidgetTypeEnum.enum.buildingImage:
      return config.buildingImageEnabled
    case HomepageWidgetTypeEnum.enum.shortcuts:
      return config.shortcutsEnabled
    case HomepageWidgetTypeEnum.enum.upcomingEvents:
      return config.upcomingEventsEnabled
    case HomepageWidgetTypeEnum.enum.announcements:
      return config.announcementsEnabled
    case HomepageWidgetTypeEnum.enum.buildingEssentials:
      return config.buildingEssentialsEnabled
    case HomepageWidgetTypeEnum.enum.services:
      return config.servicesEnabled
    case HomepageWidgetTypeEnum.enum.residentTasks:
      return config.residentTasksEnabled
    case HomepageWidgetTypeEnum.enum.chat:
      return config.chatEnabled
    case HomepageWidgetTypeEnum.enum.amenities:
      return config.amenitiesEnabled
    case HomepageWidgetTypeEnum.enum.neighborhoodPerks:
      return config.neighborhoodPerksEnabled
    case HomepageWidgetTypeEnum.enum.transport:
      return config.transportEnabled
  }
}

export const setWidgetEnabled = (
  config: HomepageConfigResponse,
  widget: HomepageWidgetType,
  enabled: boolean,
): HomepageConfigResponse => {
  switch (widget) {
    case HomepageWidgetTypeEnum.enum.buildingImage:
      return { ...config, buildingImageEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.shortcuts:
      return { ...config, shortcutsEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.upcomingEvents:
      return { ...config, upcomingEventsEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.announcements:
      return { ...config, announcementsEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.buildingEssentials:
      return { ...config, buildingEssentialsEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.services:
      return { ...config, servicesEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.residentTasks:
      return { ...config, residentTasksEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.chat:
      return { ...config, chatEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.amenities:
      return { ...config, amenitiesEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.neighborhoodPerks:
      return { ...config, neighborhoodPerksEnabled: enabled }
    case HomepageWidgetTypeEnum.enum.transport:
      return { ...config, transportEnabled: enabled }
  }
}

export const toggleWidgetEnabled = (
  config: HomepageConfigResponse,
  widget: HomepageWidgetType,
): HomepageConfigResponse => setWidgetEnabled(config, widget, !isWidgetEnabled(config, widget))

export const buildInitialWidgetOrder = (config: HomepageConfigResponse): HomepageWidgetType[] => {
  const enabled = sortByPosition(config.positions).map((position) => position.widget)
  const rest = ALL_WIDGET_TYPES.filter((widget) => !enabled.includes(widget))
  return [...enabled, ...rest]
}

// buildingImage must stay first in `order` whenever enabled, so WidgetsBlock's pinned/sortable
// split always lines up with `order[0]`.
export const normalizeWidgetOrder = (
  order: HomepageWidgetType[],
  config: HomepageConfigResponse,
): HomepageWidgetType[] => {
  if (!config.buildingImageEnabled) return order
  const rest = order.filter((widget) => widget !== HomepageWidgetTypeEnum.enum.buildingImage)
  return [HomepageWidgetTypeEnum.enum.buildingImage, ...rest]
}

// Recomputes `positions` (the only order the backend persists) from the full widget order.
// buildingImage is forced back to position 0 when enabled - the backend rejects any other slot.
export const applyWidgetOrder = (
  config: HomepageConfigResponse,
  order: HomepageWidgetType[],
): HomepageConfigResponse => {
  const enabledOrder = order.filter((widget) => isWidgetEnabled(config, widget))
  const finalOrder = config.buildingImageEnabled
    ? [
        HomepageWidgetTypeEnum.enum.buildingImage,
        ...enabledOrder.filter((widget) => widget !== HomepageWidgetTypeEnum.enum.buildingImage),
      ]
    : enabledOrder

  const positions: HomepagePosition[] = finalOrder.map((widget, position) => ({ position, widget }))
  return { ...config, positions }
}

export const isShortcutEnabled = (config: HomepageConfigResponse, type: ShortcutType): boolean =>
  config.shortcuts.some((shortcut) => shortcut.type === type)

// Blocks enabling past the cap; otherwise only flips membership in the enabled set - order is
// governed entirely by the shortcut order array.
export const toggleShortcutEnabled = (
  config: HomepageConfigResponse,
  type: ShortcutType,
): { config: HomepageConfigResponse; blocked: boolean } => {
  const enabled = isShortcutEnabled(config, type)
  if (!enabled && config.shortcuts.length >= MAX_ENABLED_SHORTCUTS) {
    return { blocked: true, config }
  }

  const shortcuts = enabled
    ? config.shortcuts.filter((shortcut) => shortcut.type !== type)
    : [...config.shortcuts, { position: config.shortcuts.length, type }]

  return { blocked: false, config: { ...config, shortcuts } }
}

export const buildInitialShortcutOrder = (config: HomepageConfigResponse): ShortcutType[] => {
  const enabled = sortByPosition(config.shortcuts).map((shortcut) => shortcut.type)
  const rest = ALL_SHORTCUT_TYPES.filter((type) => !enabled.includes(type))
  return [...enabled, ...rest]
}

export const applyShortcutOrder = (
  config: HomepageConfigResponse,
  order: ShortcutType[],
): HomepageConfigResponse => {
  const enabledOrder = order.filter((type) => isShortcutEnabled(config, type))
  const shortcuts: HomepageShortcut[] = enabledOrder.map((type, position) => ({ position, type }))
  return { ...config, shortcuts }
}

export const toggleService = (
  config: HomepageConfigResponse,
  bookingType: number,
): HomepageConfigResponse => {
  const isEnabled = config.services.some((s) => s.bookingType === bookingType)

  const services = isEnabled
    ? reindexByPosition(config.services.filter((s) => s.bookingType !== bookingType))
    : [...config.services, { bookingType, position: config.services.length }]

  return { ...config, services }
}

export const reorderServices = (
  config: HomepageConfigResponse,
  sourceIndex: number,
  targetIndex: number,
): HomepageConfigResponse => {
  const reordered = moveAndReindex(sortByPosition(config.services), sourceIndex, targetIndex)
  return reordered ? { ...config, services: reordered } : config
}

// The backend always expects the full config object, never a partial patch - it validates
// `positions.length` against the count of enabled flags.
export const buildSavePayload = (
  buildingId: number,
  config: HomepageConfigResponse,
): HomepageConfigResponse => ({
  amenities: config.amenities ?? [],
  amenitiesEnabled: config.amenitiesEnabled ?? false,
  announcementsEnabled: config.announcementsEnabled ?? false,
  buildingEssentials: config.buildingEssentials ?? [],
  buildingEssentialsEnabled: config.buildingEssentialsEnabled ?? false,
  buildingId,
  buildingImageEnabled: config.buildingImageEnabled ?? false,
  buildingImageFadeEnabled: config.buildingImageFadeEnabled ?? false,
  buildingImageFile: config.buildingImageFile ?? null,
  chatEnabled: config.chatEnabled ?? false,
  neighborhoodPerks: config.neighborhoodPerks ?? [],
  neighborhoodPerksEnabled: config.neighborhoodPerksEnabled ?? false,
  positions: config.positions ?? [],
  residentTasksEnabled: config.residentTasksEnabled ?? false,
  services: config.services ?? [],
  servicesEnabled: config.servicesEnabled ?? false,
  shortcuts: config.shortcuts ?? [],
  shortcutsEnabled: config.shortcutsEnabled ?? false,
  transportEnabled: config.transportEnabled ?? false,
  upcomingEventsEnabled: config.upcomingEventsEnabled ?? false,
})
